import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlatformApiError,
  type ReportDetail,
  type ReportQueue,
  type ReportStatus,
  type Viewer,
} from '@sfc/sdk';
import { platform } from '../api/client';
import { DetailPane, DetailSection, Split } from '../ui/DetailPane';
import { Empty } from '../ui/Empty';
import { dateTime, initials, relativeTime } from '../ui/format';
import { Icon } from '../ui/Icon';
import { StatStrip } from '../ui/StatStrip';

const REASON_LABELS: Record<string, string> = {
  spam: 'Spam',
  harassment: 'Harassment',
  scam: 'Scam',
  impersonation: 'Impersonation',
  not_a_real_idea: 'Not a real idea',
  something_else: 'Something else',
};

const SUBJECT_LABELS: Record<string, string> = {
  idea: 'Idea',
  participant: 'Person',
  message: 'Message',
};

function describe(error: unknown): string {
  if (error instanceof PlatformApiError) return error.message;
  return 'Something went wrong. Try again.';
}

/**
 * The matching app's reports, and the two things staff can do about one.
 *
 * Closing a report and acting on what it was about are separate buttons
 * because they are separate decisions: most reports are dismissed with nothing
 * happening to anybody, and one take-down may answer five of them. A single
 * "resolve" button would hide which of those happened.
 *
 * Nothing here is ever shown to the person who reported, or to the person
 * reported. There is no reply box for the same reason: the point of reporting
 * is that it is not a conversation.
 */
export function ReportsPanel({ viewer }: { viewer: Viewer }): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const mayAct = viewer.capabilities.includes('reports:act');

  const [queue, setQueue] = useState<ReportQueue>();
  const [detail, setDetail] = useState<ReportDetail>();
  const [status, setStatus] = useState('open');
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  /** Where this reader came from, since keyset paging only goes forward. */
  const [trail, setTrail] = useState<string[]>([]);
  const detailRequest = useRef<AbortController | null>(null);

  const load = useCallback(
    async (before: string | undefined, filter: string, signal?: AbortSignal) => {
      setBusy(true);
      setProblem(undefined);
      try {
        setQueue(
          await platform.staff.readReports(
            organizationId,
            { ...(filter ? { status: filter } : {}), ...(before ? { before } : {}) },
            signal,
          ),
        );
      } catch (error) {
        if (!signal?.aborted) setProblem(describe(error));
      } finally {
        if (!signal?.aborted) setBusy(false);
      }
    },
    [organizationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setQueue(undefined);
    setDetail(undefined);
    setTrail([]);
    void load(undefined, 'open', controller.signal);
    return () => {
      controller.abort();
      detailRequest.current?.abort();
    };
  }, [load]);

  function narrow(next: string): void {
    setStatus(next);
    setTrail([]);
    setDetail(undefined);
    void load(undefined, next);
  }

  async function open(reportId: string): Promise<void> {
    detailRequest.current?.abort();
    const controller = new AbortController();
    detailRequest.current = controller;
    setDetail(undefined);
    setProblem(undefined);
    try {
      setDetail(await platform.staff.readReport(organizationId, reportId, controller.signal));
    } catch (error) {
      if (!controller.signal.aborted) setProblem(describe(error));
    }
  }

  /** Runs one action, then reloads both the row and the queue behind it. */
  async function act(what: () => Promise<unknown>, reportId: string | undefined): Promise<void> {
    setProblem(undefined);
    setBusy(true);
    try {
      await what();
      if (reportId) await open(reportId);
      await load(trail.at(-1), status);
    } catch (error) {
      setProblem(describe(error));
    } finally {
      setBusy(false);
    }
  }

  const stats = useMemo(() => {
    const counts = queue?.counts;
    const open_ = counts?.open ?? 0;
    return [
      { label: 'Open', value: open_, tone: open_ > 0 ? ('down' as const) : undefined },
      { label: 'Acted on', value: counts?.actioned ?? 0 },
      { label: 'Dismissed', value: counts?.dismissed ?? 0 },
      { label: 'On this page', value: queue?.reports.length ?? 0 },
    ];
  }, [queue]);

  const rows = queue?.reports ?? [];

  return (
    <section className="card" aria-labelledby="section-heading">
      <div className="toolbar">
        <select
          className="select"
          aria-label="Filter reports by status"
          data-testid="report-status-filter"
          value={status}
          onChange={(event) => narrow(event.target.value)}
        >
          <option value="">Everything</option>
          <option value="open">Open</option>
          <option value="actioned">Acted on</option>
          <option value="dismissed">Dismissed</option>
        </select>

        <span className="toolbar__spacer" />
        <button className="button" disabled={busy} onClick={() => void load(trail.at(-1), status)}>
          <Icon name="refresh" size={16} />
          Refresh reports
        </button>
      </div>

      <StatStrip stats={stats} testId="report-stats" />

      {problem ? (
        <p className="notice notice--problem" role="alert">
          {problem}
        </p>
      ) : null}
      {busy && !queue ? (
        <p className="loading" role="status">
          Loading reports…
        </p>
      ) : null}

      <Split open={Boolean(detail)}>
        <div className="split__list">
          {queue && rows.length === 0 ? (
            <Empty
              icon="reports"
              title={status === 'open' ? 'Nothing waiting' : 'Nothing here'}
              hint="People report an idea, a person or a message from inside the matching app."
            />
          ) : null}

          <ul className="ledger" aria-label="Reports queue">
            {rows.map((report) => (
              <li
                key={report.id}
                className={`ledger__row ledger__row--inbox${detail?.report.id === report.id ? ' ledger__row--selected' : ''}${
                  report.status === 'open' ? ' ledger__row--unread' : ''
                }`}
                onClick={() => void open(report.id)}
              >
                <span className="avatar" aria-hidden="true">
                  {initials(report.reporterName)}
                </span>
                <div className="ledger__id">
                  <span className="row__title">
                    {SUBJECT_LABELS[report.subjectKind] ?? report.subjectKind} · {report.subjectLabel}
                  </span>
                  <span className="row__sub">
                    {REASON_LABELS[report.reason] ?? report.reason} · reported by {report.reporterName}
                  </span>
                </div>
                <div className="ledger__meta">
                  {report.reportsAboutSubject > 1 ? (
                    <span className="pill pill--attention" data-testid={`report-weight-${report.id}`}>
                      {report.reportsAboutSubject} reports
                    </span>
                  ) : null}
                  <span
                    className={report.status === 'open' ? 'pill pill--attention' : 'pill pill--up'}
                    data-testid={`report-status-${report.id}`}
                  >
                    {report.status}
                  </span>
                  <time className="row__sub" dateTime={report.createdAt} title={dateTime(report.createdAt)}>
                    {relativeTime(report.createdAt)}
                  </time>
                </div>
                <button
                  className="button button--small"
                  aria-label={`Read the report about ${report.subjectLabel}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    void open(report.id);
                  }}
                >
                  Read
                </button>
              </li>
            ))}
          </ul>

          {trail.length > 0 || queue?.nextCursor ? (
            <div className="pager-row">
              <button
                className="button pager"
                disabled={busy || trail.length === 0}
                data-testid="newer-reports"
                onClick={() => {
                  const shorter = trail.slice(0, -1);
                  setTrail(shorter);
                  setDetail(undefined);
                  void load(shorter.at(-1), status);
                }}
              >
                Newer reports
              </button>
              <span className="pager__where" data-testid="report-page-position">
                Page {trail.length + 1}
              </span>
              <button
                className="button pager"
                disabled={busy || !queue?.nextCursor}
                data-testid="older-reports"
                onClick={() => {
                  const cursor = queue?.nextCursor;
                  if (!cursor) return;
                  setTrail([...trail, cursor]);
                  setDetail(undefined);
                  void load(cursor, status);
                }}
              >
                Older reports
              </button>
            </div>
          ) : null}
        </div>

        {detail ? (
          <DetailPane
            label="Report details"
            recordKey={detail.report.id}
            title={`${SUBJECT_LABELS[detail.report.subjectKind] ?? detail.report.subjectKind}: ${detail.report.subjectLabel}`}
            subtitle={
              <>
                {REASON_LABELS[detail.report.reason] ?? detail.report.reason}, reported by{' '}
                {detail.report.reporterName}{' '}
                <time dateTime={detail.report.createdAt} title={dateTime(detail.report.createdAt)}>
                  {relativeTime(detail.report.createdAt).toLowerCase()}
                </time>
              </>
            }
            closeLabel="Close report"
            onClose={() => setDetail(undefined)}
          >
            <div className="detail__actions">
              <span className={detail.report.status === 'open' ? 'pill pill--attention' : 'pill pill--up'}>
                {detail.report.status}
              </span>
              {mayAct && detail.report.status === 'open' ? (
                <>
                  <button
                    className="button button--small"
                    disabled={busy}
                    data-testid="dismiss-report"
                    onClick={() => void act(() => decide(organizationId, detail.report.id, 'dismissed'), detail.report.id)}
                  >
                    Nothing in it
                  </button>
                  <button
                    className="button button--small"
                    disabled={busy}
                    data-testid="close-report"
                    onClick={() => void act(() => decide(organizationId, detail.report.id, 'actioned'), detail.report.id)}
                  >
                    Dealt with
                  </button>
                </>
              ) : null}
            </div>

            {detail.report.detail ? (
              <DetailSection title="What they said">
                <p className="detail__text" data-testid="report-detail-text">
                  {detail.report.detail}
                </p>
              </DetailSection>
            ) : null}

            {detail.idea ? (
              <DetailSection title="The idea">
                <p className="row__title">{detail.idea.title}</p>
                <p className="detail__text">{detail.idea.pitch}</p>
                <p className="row__sub">
                  {detail.idea.stage} · {detail.idea.compensationType} · looking for{' '}
                  {detail.idea.skills.map((skill) => skill.label).join(', ') || 'nobody in particular'}
                </p>
                <p className="row__sub">
                  Posted by {detail.idea.authorName} ({detail.idea.authorEmail})
                  {detail.idea.authorDisabled ? ' · stopped' : ''} ·{' '}
                  <span data-testid="reported-idea-status">{detail.idea.status}</span>
                </p>
                {mayAct ? (
                  <div className="detail__actions">
                    <button
                      className="button button--small button--danger"
                      disabled={busy || detail.idea.status === 'removed'}
                      data-testid="take-down-idea"
                      onClick={() => {
                        const ideaId = detail.idea!.id;
                        void act(() => platform.staff.removeIdea(organizationId, ideaId), detail.report.id);
                      }}
                    >
                      {detail.idea.status === 'removed' ? 'Already down' : 'Take it down'}
                    </button>
                    <AccessButton
                      organizationId={organizationId}
                      participantId={detail.idea.authorId}
                      name={detail.idea.authorName}
                      disabled={detail.idea.authorDisabled}
                      busy={busy}
                      onDone={(run) => void act(run, detail.report.id)}
                    />
                  </div>
                ) : null}
              </DetailSection>
            ) : null}

            {detail.participant ? (
              <DetailSection title="The person">
                <p className="row__title">{detail.participant.displayName}</p>
                <p className="row__sub">{detail.participant.email}</p>
                {detail.participant.headline ? (
                  <p className="detail__text">{detail.participant.headline}</p>
                ) : null}
                <p className="row__sub">
                  {detail.participant.liveIdeas} live idea
                  {detail.participant.liveIdeas === 1 ? '' : 's'} · joined{' '}
                  {relativeTime(detail.participant.joinedAt).toLowerCase()}
                  {detail.participant.disabled ? ' · stopped' : ''}
                </p>
                {mayAct ? (
                  <div className="detail__actions">
                    <AccessButton
                      organizationId={organizationId}
                      participantId={detail.participant.id}
                      name={detail.participant.displayName}
                      disabled={detail.participant.disabled}
                      busy={busy}
                      onDone={(run) => void act(run, detail.report.id)}
                    />
                  </div>
                ) : null}
              </DetailSection>
            ) : null}

            {detail.message ? (
              <DetailSection title="The message, in context">
                <p className="row__sub">About “{detail.message.ideaTitle}”</p>
                <ul className="transcript" data-testid="reported-message-context">
                  {detail.message.context.map((line) => (
                    <li
                      key={line.id}
                      className={`transcript__line${line.id === detail.message!.id ? ' transcript__line--flagged' : ''}`}
                    >
                      <span className="row__sub">
                        {line.senderName} · {dateTime(line.sentAt)}
                      </span>
                      <p className="detail__text">
                        {line.erased ? 'No longer kept.' : line.body}
                      </p>
                    </li>
                  ))}
                </ul>
                {mayAct ? (
                  <div className="detail__actions">
                    <AccessButton
                      organizationId={organizationId}
                      participantId={detail.message.senderId}
                      name={detail.message.senderName}
                      disabled={detail.message.senderDisabled}
                      busy={busy}
                      onDone={(run) => void act(run, detail.report.id)}
                    />
                  </div>
                ) : null}
              </DetailSection>
            ) : null}
          </DetailPane>
        ) : null}
      </Split>
    </section>
  );
}

function decide(organizationId: string, reportId: string, status: ReportStatus) {
  return platform.staff.decideReport(organizationId, reportId, status);
}

/**
 * Stopping somebody, or letting them back.
 *
 * Asks first. It ends the session they are holding right now and is done to a
 * member of the public who will not be told who decided, which is a thing to
 * be sure about before clicking.
 */
function AccessButton({
  organizationId,
  participantId,
  name,
  disabled,
  busy,
  onDone,
}: {
  organizationId: string;
  participantId: string;
  name: string;
  disabled: boolean;
  busy: boolean;
  onDone: (run: () => Promise<unknown>) => void;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <button
        className={`button button--small${disabled ? '' : ' button--danger'}`}
        disabled={busy}
        data-testid={disabled ? 'enable-participant' : 'disable-participant'}
        onClick={() => setConfirming(true)}
      >
        {disabled ? `Let ${name} back in` : `Stop ${name} signing in`}
      </button>
    );
  }

  return (
    <>
      <span className="row__sub">
        {disabled
          ? `${name} will be able to sign in again.`
          : `${name} loses the session they are holding now. What they wrote stays.`}
      </span>
      <button
        className="button button--small button--danger"
        disabled={busy}
        data-testid="confirm-participant-access"
        onClick={() => {
          setConfirming(false);
          onDone(() =>
            platform.staff.setParticipantAccess(organizationId, participantId, !disabled),
          );
        }}
      >
        Yes, {disabled ? 'let them back' : 'stop them'}
      </button>
      <button className="button button--small" disabled={busy} onClick={() => setConfirming(false)}>
        Cancel
      </button>
    </>
  );
}
